/**
 * AI Insights — Iter-106 (Decision Cockpit, not a Dashboard)
 *
 *  1. Executive Action Cards   — 3 actions recommended this week
 *  2. Payment Risk Heatmap     — customer grid + explanation drawer
 *  3. Cashflow Forecast        — 30/60/90 bars + confidence shading
 *  4. Vendor Savings Table     — ranked, actionable
 */
import React, { useEffect, useMemo, useState } from "react";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import {
  Brain, AlertTriangle, TrendingUp, DollarSign, ShoppingCart,
  Loader2, X, Target, Sparkles, ArrowRight, Activity, Wallet,
  Lightbulb, ChevronRight, Award,
} from "lucide-react";

const SAR = (n) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: "SAR", maximumFractionDigits: 0,
}).format(Number(n || 0));

const PCT0 = (n) => `${Math.round(Number(n || 0) * 100)}%`;
const PCT1 = (n) => `${Number(n || 0).toFixed(1)}%`;

const RISK_TONE = {
  very_high: "bg-rose-500 text-white",
  high:      "bg-orange-400 text-white",
  medium:    "bg-amber-300 text-amber-900",
  low:       "bg-emerald-200 text-emerald-900",
};
const RISK_BORDER = {
  very_high: "border-rose-600",
  high:      "border-orange-500",
  medium:    "border-amber-400",
  low:       "border-emerald-400",
};

// ─── 1. Executive Action Cards ───────────────────────────────────

const ActionCard = ({ icon: Icon, color, title, body, value, cta, onClick }) => (
  <button
    type="button"
    data-testid={`ai-action-${title.replace(/\s+/g, "-").toLowerCase()}`}
    onClick={onClick}
    className={`group flex flex-col text-right rounded-2xl border-2 border-${color}-300 bg-gradient-to-br from-${color}-50 to-white p-5 shadow-sm transition-all hover:shadow-lg hover:-translate-y-0.5`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className={`flex h-11 w-11 items-center justify-center rounded-xl bg-${color}-500 text-white shadow-md`}>
        <Icon className="h-5 w-5" />
      </div>
      <span className={`rounded-full bg-${color}-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-${color}-700`}>
        موصى به
      </span>
    </div>
    <div className="mt-4 text-xs font-medium uppercase tracking-wide text-slate-500">{title}</div>
    <div className="mt-1 text-xl font-bold text-slate-900">{value}</div>
    <p className="mt-2 text-xs text-slate-600 leading-relaxed">{body}</p>
    <div className={`mt-auto pt-3 inline-flex items-center gap-1 text-xs font-semibold text-${color}-700 group-hover:gap-2 transition-all`}>
      {cta} <ChevronRight className="h-3.5 w-3.5" />
    </div>
  </button>
);

// ─── 2. Risk Heatmap + Drawer ────────────────────────────────────

const RiskTile = ({ row, onClick }) => {
  const tone = RISK_TONE[row.risk_bucket] || RISK_TONE.low;
  const border = RISK_BORDER[row.risk_bucket] || RISK_BORDER.low;
  return (
    <button
      data-testid={`risk-tile-${row.customer_id}`}
      onClick={onClick}
      className={`relative rounded-xl border-2 ${border} ${tone} p-3 text-right transition-all hover:scale-105 hover:shadow-lg cursor-pointer`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wide opacity-90">{row.risk_bucket}</span>
        <span className="text-xs font-mono font-bold">{PCT0(row.probability)}</span>
      </div>
      <div className="mt-2 text-sm font-bold truncate">{row.customer_name}</div>
      <div className="mt-1 text-[11px] opacity-80">{SAR(row.expected_loss)}</div>
    </button>
  );
};

const RiskDrawer = ({ row, onClose }) => {
  if (!row) return null;
  const factors = Object.entries(row.explanation || {})
    .filter(([k]) => !["bias", "logit_sum"].includes(k));
  return (
    <div className="fixed inset-y-0 right-0 z-40 w-full max-w-md overflow-y-auto border-l border-slate-200 bg-white shadow-2xl"
          data-testid="risk-drawer" dir="rtl">
      <div className="flex items-center justify-between border-b border-slate-200 p-4">
        <h3 className="text-lg font-bold text-slate-900">شرح درجة المخاطر</h3>
        <button onClick={onClose} className="rounded-full p-1 hover:bg-slate-100">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-4 p-5">
        <div>
          <div className="text-xs uppercase text-slate-500">العميل</div>
          <div className="mt-1 text-2xl font-bold text-slate-900">{row.customer_name}</div>
          <div className="mt-2 flex items-center gap-2">
            <span className={`rounded-full px-3 py-1 text-sm font-bold ${RISK_TONE[row.risk_bucket]}`}>
              {PCT0(row.probability)} · {row.risk_bucket}
            </span>
            <span className="text-xs text-slate-500">ثقة: {row.confidence}</span>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="text-xs font-semibold text-slate-600">الخسارة المتوقعة</div>
          <div className="mt-1 text-xl font-bold text-rose-700">{SAR(row.expected_loss)}</div>
          <div className="mt-1 text-xs text-slate-500">من إجمالي {SAR(row.open_amount)} مفتوح</div>
        </div>

        {/* Factor contributions */}
        <div>
          <div className="text-xs font-bold uppercase text-slate-600 mb-2">العوامل المُساهمة (شفافة + قابلة للتدقيق)</div>
          <ul className="space-y-2" data-testid="risk-factors">
            {factors.map(([k, v]) => {
              const label = ({
                avg_delay_factor:      "متوسط تأخير الدفع",
                oldest_overdue_factor: "أقدم تأخير حالي",
                utilization_factor:    "استخدام الائتمان",
                disputes_factor:       "النزاعات",
                dso_factor:            "DSO",
              })[k] || k;
              const positive = v >= 0;
              const magnitude = Math.min(100, Math.abs(v) * 50);
              return (
                <li key={k} className="text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-700">{label}</span>
                    <span className={`font-mono font-bold ${positive ? "text-rose-700" : "text-emerald-700"}`}>
                      {positive ? "+" : ""}{v.toFixed(2)}
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                    <div className={`h-full ${positive ? "bg-rose-400" : "bg-emerald-400"}`}
                          style={{ width: `${magnitude}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
          <div className="flex items-center gap-2 font-semibold text-slate-700 mb-1">
            <Sparkles className="h-3.5 w-3.5 text-violet-600" /> طريقة الحساب
          </div>
          المعادلة: <code className="bg-slate-100 px-1 rounded">sigmoid(Σ factors − bias)</code><br />
          مجموع logit: <code className="bg-slate-100 px-1 rounded">{row.explanation?.logit_sum}</code>
        </div>
      </div>
    </div>
  );
};

// ─── 3. Cashflow Forecast ────────────────────────────────────────

const CashflowChart = ({ forecast }) => {
  if (!forecast) return null;
  const buckets = [
    { key: "30d",   label: "30 يوم", color: "emerald" },
    { key: "60d",   label: "60 يوم", color: "sky"     },
    { key: "90d",   label: "90 يوم", color: "violet"  },
    { key: "later", label: "بعد 90", color: "slate"   },
  ];
  const max = Math.max(1, ...Object.values(forecast.projected_inflows));
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900">
          <TrendingUp className="h-4 w-4 text-violet-600" />
          توقع التدفق النقدي
        </h3>
        <span className="text-xs text-slate-500">
          عامل الثقة: <span className="font-bold text-emerald-700">{PCT0(forecast.decay_factor)}</span>
        </span>
      </div>
      <div className="grid grid-cols-4 gap-3" data-testid="cashflow-bars">
        {buckets.map(b => {
          const v = forecast.projected_inflows[b.key] || 0;
          const raw = forecast.raw_buckets[b.key] || 0;
          const h = (v / max) * 100;
          return (
            <div key={b.key} className={`rounded-xl border border-${b.color}-200 bg-${b.color}-50 p-3`}>
              <div className={`text-[10px] uppercase tracking-wide text-${b.color}-700`}>{b.label}</div>
              <div className="mt-1 h-24 flex items-end">
                <div className={`w-full bg-${b.color}-500 rounded-t transition-all`}
                      style={{ height: `${h}%`, minHeight: "4px" }} />
              </div>
              <div className={`mt-2 text-base font-bold text-${b.color}-900`}>{SAR(v)}</div>
              <div className="text-[10px] text-slate-500">من {SAR(raw)} متوقع</div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
        <div className="rounded-lg bg-emerald-50 p-2">
          <div className="text-slate-500">تراكمي 30 يوم</div>
          <div className="mt-1 font-bold text-emerald-700">{SAR(forecast.total_30d)}</div>
        </div>
        <div className="rounded-lg bg-sky-50 p-2">
          <div className="text-slate-500">تراكمي 60 يوم</div>
          <div className="mt-1 font-bold text-sky-700">{SAR(forecast.total_60d)}</div>
        </div>
        <div className="rounded-lg bg-violet-50 p-2">
          <div className="text-slate-500">تراكمي 90 يوم</div>
          <div className="mt-1 font-bold text-violet-700">{SAR(forecast.total_90d)}</div>
        </div>
      </div>
    </div>
  );
};

// ─── 4. Vendor Savings Table ─────────────────────────────────────

const VendorSavings = ({ deviation }) => {
  if (!deviation) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900">
          <ShoppingCart className="h-4 w-4 text-violet-600" />
          فرص التفاوض مع الموردين
        </h3>
        <span className="text-xs text-slate-500">
          {deviation.opportunities.length} فرصة من {deviation.items_analysed} صنف
        </span>
      </div>
      <div className="overflow-x-auto">
        <table data-testid="vendor-savings-table" className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-right">الصنف</th>
              <th className="px-3 py-2 text-right">عروض</th>
              <th className="px-3 py-2 text-right">أفضل مورد</th>
              <th className="px-3 py-2 text-right">أعلى سعر</th>
              <th className="px-3 py-2 text-center">الفجوة</th>
              <th className="px-3 py-2 text-center">التوفير</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {deviation.opportunities.slice(0, 15).map((o, i) => (
              <tr key={i} className="hover:bg-violet-50/50">
                <td className="px-3 py-2 max-w-[200px] truncate" title={o.item_description}>
                  {o.item_description}
                  <div className="text-[10px] text-slate-400">{o.uom || "—"}</div>
                </td>
                <td className="px-3 py-2 text-center font-mono">{o.quotes_count}</td>
                <td className="px-3 py-2">
                  <div className="font-medium text-emerald-700">{o.best_vendor.supplier_name}</div>
                  <div className="text-[10px] font-mono">{SAR(o.best_vendor.price)}</div>
                </td>
                <td className="px-3 py-2">
                  <div className="font-medium text-rose-700">{o.worst_vendor.supplier_name}</div>
                  <div className="text-[10px] font-mono">{SAR(o.worst_vendor.price)} (z={o.worst_vendor.z_score})</div>
                </td>
                <td className="px-3 py-2 text-center">
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-800 font-bold">
                    {PCT1(o.spread_pct)}
                  </span>
                </td>
                <td className="px-3 py-2 text-center">
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-800 font-bold">
                    {o.savings_if_negotiated}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Main page ───────────────────────────────────────────────────

export default function AIInsights() {
  const [summary, setSummary]   = useState(null);
  const [riskData, setRisk]     = useState(null);
  const [forecast, setForecast] = useState(null);
  const [deviation, setDev]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [drawerRow, setDrawerRow] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, r, f, d] = await Promise.all([
          axiosInstance.get("/ai/summary"),
          axiosInstance.get("/ai/payment-risk?limit=24"),
          axiosInstance.get("/ai/cashflow-forecast?days=90"),
          axiosInstance.get("/ai/vendor-deviation?limit=30"),
        ]);
        setSummary(s.data); setRisk(r.data);
        setForecast(f.data); setDev(d.data);
      } catch (e) {
        toast.error("فشل تحميل AI Insights");
      } finally { setLoading(false); }
    })();
  }, []);

  // Derive 3 recommended actions from the data.
  const actions = useMemo(() => {
    if (!summary || !riskData || !deviation) return [];
    const items = [];
    if ((summary.customers_very_high_risk || 0) > 0) {
      const top = (riskData.items || []).find(x => x.risk_bucket === "very_high");
      items.push({
        icon: AlertTriangle, color: "rose",
        title: "تواصل مع عملاء عالي الخطر",
        value: SAR(summary.expected_loss_30d),
        body: `${summary.customers_very_high_risk} عميل احتمال تأخر دفعهم ≥75%. أعلى خسارة متوقعة: ${top?.customer_name || "—"}.`,
        cta:  "افتح خريطة المخاطر",
        action: () => document.getElementById("risk-heatmap")?.scrollIntoView({ behavior: "smooth" }),
      });
    }
    if ((deviation.opportunities || []).length > 0) {
      const top = deviation.opportunities[0];
      items.push({
        icon: Award, color: "emerald",
        title: "وفّر على المشتريات",
        value: top.savings_if_negotiated,
        body: `${deviation.opportunities.length} صنف بفجوات سعرية كبيرة. أعلى توفير على "${top.item_description.slice(0, 30)}".`,
        cta:  "افتح جدول التوفير",
        action: () => document.getElementById("vendor-savings")?.scrollIntoView({ behavior: "smooth" }),
      });
    }
    items.push({
      icon: TrendingUp, color: "violet",
      title: "السيولة المتوقعة 90 يوم",
      value: SAR(summary.projected_inflow_90d),
      body: `معدّل التحصيل التاريخي ${PCT0(summary.decay_factor)}. خطّط لتدفقات الـ 30/60/90 يوم بثقة.`,
      cta:  "افتح توقع السيولة",
      action: () => document.getElementById("cashflow-forecast")?.scrollIntoView({ behavior: "smooth" }),
    });
    return items;
  }, [summary, riskData, deviation]);

  return (
    <div data-testid="ai-insights-page" className="space-y-6 p-6" dir="rtl">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">
          <Brain className="inline -mt-1 ml-2 h-7 w-7 text-violet-600" />
          AI Decision Cockpit
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          غرفة قرار ذكية — مخاطر العملاء · توقع السيولة · فرص التفاوض · جميعها شفافة وقابلة للتدقيق
        </p>
      </div>

      {loading && (
        <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white py-16 text-slate-500">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          جارٍ تحليل البيانات…
        </div>
      )}

      {!loading && (
        <>
          {/* Executive Action Cards */}
          {actions.length > 0 && (
            <section data-testid="ai-actions">
              <div className="mb-3 flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-amber-500" />
                <h2 className="text-sm font-bold uppercase tracking-wide text-slate-700">
                  {actions.length} إجراء موصى به هذا الأسبوع
                </h2>
              </div>
              <div className="grid gap-3 md:grid-cols-3">
                {actions.map((a, i) => (
                  <ActionCard key={i} {...a} onClick={a.action} />
                ))}
              </div>
            </section>
          )}

          {/* Risk Heatmap */}
          {riskData && (
            <section id="risk-heatmap" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900">
                  <Target className="h-4 w-4 text-violet-600" />
                  خريطة مخاطر العملاء
                </h3>
                <div className="flex items-center gap-2 text-xs">
                  <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-rose-500" />عالي جداً</span>
                  <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-orange-400" />عالي</span>
                  <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-amber-300" />متوسط</span>
                  <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-emerald-200" />منخفض</span>
                </div>
              </div>
              {riskData.items.length === 0 ? (
                <div className="py-8 text-center text-sm text-slate-400">
                  لا يوجد عملاء بفواتير مفتوحة لتحليلهم.
                </div>
              ) : (
                <div data-testid="risk-heatmap-grid"
                      className="grid gap-2 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                  {riskData.items.map(row => (
                    <RiskTile key={row.customer_id} row={row}
                                onClick={() => setDrawerRow(row)} />
                  ))}
                </div>
              )}
              <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                <span>الطريقة: {riskData.method}</span>
                <span>إجمالي الخسارة المتوقعة: <span className="font-bold text-rose-700">{SAR(riskData.total_expected_loss)}</span></span>
              </div>
            </section>
          )}

          {/* Cashflow Forecast */}
          <section id="cashflow-forecast">
            <CashflowChart forecast={forecast} />
          </section>

          {/* Vendor Savings */}
          <section id="vendor-savings">
            <VendorSavings deviation={deviation} />
          </section>
        </>
      )}

      {drawerRow && <RiskDrawer row={drawerRow} onClose={() => setDrawerRow(null)} />}
    </div>
  );
}
